import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  customerFormDefaults,
  customerFormSchema,
  type CustomerFormValues,
} from '../../schemas/customerForm';
import styles from '../BookingWidget.module.css';

interface CustomerStepProps {
  onSubmit: (values: CustomerFormValues) => void;
  isSubmitting: boolean;
  serverError: string | null;
}

export function CustomerStep({
  onSubmit,
  isSubmitting,
  serverError,
}: CustomerStepProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: customerFormDefaults,
    mode: 'onSubmit',
  });

  return (
    <form
      id="yoga-customer-form"
      className={styles.form}
      onSubmit={handleSubmit((values) => {
        onSubmit({
          ...values,
          phone: values.phone?.trim() ? values.phone.trim() : undefined,
        });
      })}
      noValidate
    >
      {serverError ? <p className={styles.error}>{serverError}</p> : null}

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="firstName">
            Vorname
          </label>
          <input
            id="firstName"
            className={styles.input}
            autoComplete="given-name"
            disabled={isSubmitting}
            {...register('firstName')}
          />
          {errors.firstName ? (
            <span className={styles.fieldError}>{errors.firstName.message}</span>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="lastName">
            Nachname
          </label>
          <input
            id="lastName"
            className={styles.input}
            autoComplete="family-name"
            disabled={isSubmitting}
            {...register('lastName')}
          />
          {errors.lastName ? (
            <span className={styles.fieldError}>{errors.lastName.message}</span>
          ) : null}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="email">
          E-Mail
        </label>
        <input
          id="email"
          type="email"
          className={styles.input}
          autoComplete="email"
          disabled={isSubmitting}
          {...register('email')}
        />
        {errors.email ? (
          <span className={styles.fieldError}>{errors.email.message}</span>
        ) : null}
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="phone">
          Telefon (optional)
        </label>
        <input
          id="phone"
          type="tel"
          className={styles.input}
          autoComplete="tel"
          disabled={isSubmitting}
          {...register('phone')}
        />
        {errors.phone ? (
          <span className={styles.fieldError}>{errors.phone.message}</span>
        ) : null}
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="street">
            Strasse
          </label>
          <input
            id="street"
            className={styles.input}
            autoComplete="address-line1"
            disabled={isSubmitting}
            {...register('street')}
          />
          {errors.street ? (
            <span className={styles.fieldError}>{errors.street.message}</span>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="streetNumber">
            Nr.
          </label>
          <input
            id="streetNumber"
            className={styles.input}
            disabled={isSubmitting}
            {...register('streetNumber')}
          />
          {errors.streetNumber ? (
            <span className={styles.fieldError}>{errors.streetNumber.message}</span>
          ) : null}
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="postalCode">
            PLZ
          </label>
          <input
            id="postalCode"
            className={styles.input}
            autoComplete="postal-code"
            disabled={isSubmitting}
            {...register('postalCode')}
          />
          {errors.postalCode ? (
            <span className={styles.fieldError}>{errors.postalCode.message}</span>
          ) : null}
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="city">
            Ort
          </label>
          <input
            id="city"
            className={styles.input}
            autoComplete="address-level2"
            disabled={isSubmitting}
            {...register('city')}
          />
          {errors.city ? (
            <span className={styles.fieldError}>{errors.city.message}</span>
          ) : null}
        </div>
      </div>

      <input type="hidden" {...register('country')} value="CH" />
    </form>
  );
}

export type { CustomerFormValues };
